import { JsonError } from '@sagold/react-json-editor';
import { v4 as uuidv4 } from 'uuid';

import { getFileNameAndExtensionFromName } from '../../utils/getFileExtensions';

export type UploadState =
  | 'isPending'
  | 'isError'
  | 'isSuccess'
  | 'initial'
  | 'isFetchingMetadata';

export type FileSelection = {
  file: File;
  id: string;
  uploadState: UploadState;
  error?: string;
  validationErrors?: JsonError[];
  originalFileExtention: string;
  originalFileName: string;
  extractedMetaData?: {
    isInitial: boolean;
    value: string | string[];
  };
};
export type FileSelectionState = FileSelection[];

type AddFileSelections = {
  type: 'addFileSelections';
  payload: File[];
};
type AddFileSelectionsWithExtractedMetaData = {
  type: 'addFileSelectionsWithMetadata';
  payload: { file: File; metaData?: string }[];
};
type RemoveFileSelections = {
  type: 'removeFileSelections';
  payload: string[];
};
type ResetFileSelections = {
  type: 'resetFileSelections';
};
type UpdateFileSelectionUploadState = {
  type: 'updateFileSelectionUploadState';
  payload: {
    id: string;
    newState: UploadState;
    error?: string;
  };
};
type UpdateFileMetaData = {
  type: 'updateFileMetaData';
  payload: {
    id: string;
    value: string | string[];
  };
};
type UpdateAllUploadStates = {
  type: 'updateAllUploadStates';
  payload: { newState: UploadState };
};
type ReplaceFileValidationErrors = {
  type: 'replaceFileValidationErrors';
  payload: {
    id: string;
    validationErrors: JsonError[];
    errorsCodesToKeep?: Set<string>;
  };
};
type ClearAllValidationErrors = {
  type: 'clearAllValidationErrors';
};
export type UpdateFileNamePayload = {
  id: string;
  name: string;
  cleared?: boolean;
};
type UpdateFileName = {
  type: 'updateFileName';
  payload: UpdateFileNamePayload;
};

export type FileSelectionAction =
  | AddFileSelections
  | AddFileSelectionsWithExtractedMetaData
  | RemoveFileSelections
  | ResetFileSelections
  | UpdateFileSelectionUploadState
  | UpdateAllUploadStates
  | UpdateFileMetaData
  | ReplaceFileValidationErrors
  | ClearAllValidationErrors
  | UpdateFileName;

export const fileReducer = (
  state: FileSelectionState,
  action: FileSelectionAction
): FileSelectionState => {
  switch (action.type) {
    case 'addFileSelections': {
      return [
        ...state,
        ...action.payload.map(
          (file): FileSelection => ({
            file,
            uploadState: 'initial',
            id: uuidv4(),
            ...getFileNameAndExtensionFromName(file.name),
          })
        ),
      ];
    }
    case 'addFileSelectionsWithMetadata': {
      return [
        ...state,
        ...action.payload.map(
          (data): FileSelection => ({
            file: data?.file,
            uploadState: 'initial',
            id: uuidv4(),
            extractedMetaData: {
              isInitial: true,
              value: data?.metaData || '',
            },
            ...getFileNameAndExtensionFromName(data.file.name),
          })
        ),
      ];
    }
    case 'removeFileSelections': {
      return state.filter(
        (selection) => !action.payload.includes(selection.id)
      );
    }
    case 'resetFileSelections': {
      return [];
    }
    case 'updateFileMetaData': {
      return state.map((file) =>
        file.id === action.payload.id
          ? {
              ...file,
              extractedMetaData: {
                isInitial: false,
                value: action.payload.value,
              },
            }
          : file
      );
    }
    case 'updateFileSelectionUploadState': {
      return state.map((selection) =>
        selection.id === action.payload.id
          ? {
              ...selection,
              uploadState: action.payload.newState,
              error: action.payload.error ?? undefined,
            }
          : selection
      );
    }
    case 'updateAllUploadStates': {
      return state.map((s) => ({ ...s, uploadState: action.payload.newState }));
    }
    case 'replaceFileValidationErrors': {
      return state.map((file) =>
        file.id === action.payload.id
          ? {
              ...file,
              validationErrors: [
                ...(file.validationErrors?.filter((vE) =>
                  action.payload.errorsCodesToKeep?.has(vE.code)
                ) || []),
                ...action.payload.validationErrors,
              ],
            }
          : file
      );
    }
    case 'clearAllValidationErrors': {
      return state.map((file) => {
        const { validationErrors: _, ...rest } = file;
        return rest;
      });
    }
    case 'updateFileName': {
      return state.map((fileState) => {
        return fileState.id === action.payload.id
          ? {
              ...fileState,
              file: new File(
                [fileState.file],
                `${action.payload.name}${fileState.originalFileExtention}`,
                {
                  type: fileState.file.type,
                  lastModified: fileState.file.lastModified,
                }
              ),
            }
          : fileState;
      });
    }
    default: {
      const exhaustionCheck: never = action;
      console.error(
        'Unexpected action type in function "fileReducer":',
        exhaustionCheck
      );

      return state;
    }
  }
};
